import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { handleGetLogsData } from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import {
    getMenuItemById,
    defaultOrgId,
    createTranslation,
    interpolateTranslation,
    hasOrgPermission,
    passlyFormatDate,
    TIME_DATE_FORMAT
} from '@app/helpers';
import { ITableHead, IListPageBarEnabled, IActiveFilter, FilterEnum, ILogsValue, ILogsFlattened, IFilterSelect } from '@app/models';
import { Link, TableCell, Typography, Collapse } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import { UAParser } from 'ua-parser-js';
import { PasslyTable } from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import { RawEventComponent } from '@app/pages/authManager/components';
import { Audit } from '@app/store/constants';
import { PasslyTooltip } from '@app/componentsV2';

interface ILogsTableData {
    rawObject: ILogsFlattened;
    raw: string;
    created: Date;
    id: string;
    opened?: boolean;
}

enum TableHeadEnum {
    accordion = 'accordion',
    fullName = 'fullName',
    auditTimeStamp = 'created',
    location = 'rawObject',
    ipAddress = 'ipAddress',
    auditSeverity = 'auditSeverity',
    signinMethod = 'signinMethod',
    warning = 'warning'
}

const LogsPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Logs);
    const takeCount = 100;

    const [menuItem] = useState<IMenuItem | undefined>(item);

    const [rows, setRows] = useState<ILogsTableData[]>([]);
    const [continuationToken, setContinuationToken] = useState<string>(''); // this will be needed for paging and will go on the querystring as &token=.

    const logs = useSelector((state: any) => state.logs.logs);
    const logsLoading = useSelector((state: any) => state.logs.logsLoading);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: false,
        organizationsEnabled: true
    };

    const onExpandClick = (index: number) => () => {
        const rowId: number = index;
        let newRows = [...rows];

        newRows[rowId].opened = !newRows[rowId].opened;
        setRows(newRows);
    };

    const getComplexExpandCell = (row: ILogsTableData, index: number) => {
        return (
            <TableCell className='no-vertical-align' onClick={onExpandClick(index)}>
                {row.opened ? <i className='fa-regular fa-angle-up accordion__icon' /> : <i className='fa-regular fa-angle-down accordion__icon' />}
            </TableCell>
        );
    };

    const getComplexTimestampCell = (row: ILogsTableData, index: number) => {
        return (
            <TableCell onClick={onExpandClick(index)}>
                {row.created ? passlyFormatDate(row.created, TIME_DATE_FORMAT) : translation('DATE_NEVER')};
            </TableCell>
        );
    };
    const accordionContent = (row: ILogsTableData, index: number) => {
        return (
            <>
                <div onClick={onExpandClick(index)}>
                    <div className='accordion__detail'>
                        <img src={getOSImage(row.rawObject.trace_userAgent)} />
                        <span>{getOS(row.rawObject.trace_userAgent)}</span>
                    </div>
                    <div className='accordion__detail'>
                        <img src={getBrowserImage(row.rawObject.trace_userAgent)} />
                        <span>{getBrowser(row.rawObject.trace_userAgent)}</span>
                    </div>
                </div>
                <div>
                    <Link onClick={() => showRawEvent(row.rawObject)}>{translation('VIEW_RAW_EVENT')}</Link>
                </div>
            </>
        );
    };
    const getComplexFullNameCell = (row: ILogsTableData, index: number) => {
        return (
            <TableCell>
                <div className='user__name' onClick={onExpandClick(index)}>
                    <PasslyTooltip title={getProperty(row.rawObject, 'record_impersonating_nameid record_identity_nameid principal_nameid')}>
                        <span>{getProperty(row.rawObject, 'record_impersonating_nameid record_identity_nameid principal_nameid')}</span>
                    </PasslyTooltip>
                    {getProperty(row.rawObject, 'record_identity_obo record_identity_impersonating_nameid') !== undefined &&
                        getProperty(row.rawObject, 'record_identity_obo record_identity_impersonating_nameid') !== '' && (
                            <>
                                {interpolateTranslation(translation('LOGS_JITS_2FA_BY'), [
                                    getProperty(row.rawObject, 'record_identity_obo record_identity_impersonating_nameid')
                                ])}
                            </>
                        )}
                    <br />
                    <Typography variant='body1'>
                        {translation(row.rawObject.record_message)}{' '}
                        {translation(row.rawObject.record_source) === '' ? row.rawObject.record_source : translation(row.rawObject.record_source)}
                    </Typography>
                </div>
                <Collapse in={row.opened} timeout='auto' unmountOnExit>
                    {accordionContent(row, index)}
                </Collapse>
            </TableCell>
        );
    };
    const getComplexLocationCell = (row: ILogsTableData, index: number) => {
        return <TableCell onClick={onExpandClick(index)}>{getLocationString(row.rawObject)}</TableCell>;
    };
    const getComplexIpCell = (row: ILogsTableData, index: number) => {
        return <TableCell onClick={onExpandClick(index)}>{row.rawObject.trace_clientIp_ip}</TableCell>;
    };

    const getComplexSeverityCell = (row: ILogsTableData, index: number) => {
        return <TableCell onClick={onExpandClick(index)}>{row.rawObject.severity}</TableCell>;
    };

    const getComplexAuthmethodCell = (row: ILogsTableData, index: number) => {
        return (
            <TableCell onClick={onExpandClick(index)}>
                {row.rawObject.record_identity_authmethod !== undefined && (
                    <div>{translation(createTranslation('AUDIT_AUTH_METHOD_' + row.rawObject.record_identity_authmethod.toUpperCase()))}</div>
                )}
            </TableCell>
        );
    };

    const getComplexWarningCell = (row: ILogsTableData, index: number) => {
        return row.rawObject.success.toLowerCase() === 'false' ? (
            <TableCell onClick={onExpandClick(index)}>
                <ErrorIcon className='error--icon' />
            </TableCell>
        ) : (
            <TableCell />
        );
    };


    const severityFilterDropdown: IFilterSelect[] = [
        {
            id: 0,
            label: 'EVERYTHING',
            value: 'Everything'
        },
        {
            id: 1,
            label: 'SEVERITY_INFORMATION',
            value: 'Information'
        },
        {
            id: 2,
            label: 'SEVERITY_WARNING',
            value: 'Warning'
        },
        {
            id: 3,
            label: 'SEVERITY_CRITICAL',
            value: 'Critical'
        }
    ];

    const signinMethodFilterDropdown: IFilterSelect[] = [
        {
            id: 0,
            label: 'EVERYTHING',
            value: 'Everything'
        },
        {
            id: 1,
            label: 'AUDIT_AUTH_METHOD_PASSWORD',
            value: 'password'
        },
        {
            id: 2,
            label: 'AUDIT_AUTH_METHOD_AD',
            value: 'ad'
        },
        {
            id: 3,
            label: 'AUDIT_AUTH_METHOD_ONBOARDING',
            value: 'onboarding'
        },
        {
            id: 4,
            label: 'AUDIT_AUTH_METHOD_OTP',
            value: 'otp'
        },
        {
            id: 5,
            label: 'AUDIT_AUTH_METHOD_PUSH',
            value: 'push'
        },
        {
            id: 6,
            label: 'AUDIT_AUTH_METHOD_U2F',
            value: 'u2f'
        }
    ];

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.accordion,
            label: '',
            type: 'getComplexCell',
            width: '56px',
            getComplexCell: getComplexExpandCell
        },
        {
            id: TableHeadEnum.fullName,
            label: 'FULL_NAME',
            type: 'getComplexCell',
            getComplexCell: getComplexFullNameCell,
        },
        {
            id: TableHeadEnum.auditTimeStamp,
            label: 'AUDIT_TIMESTAMP',
            type: 'getComplexCell',
            getComplexCell: getComplexTimestampCell
        },
        {
            id: TableHeadEnum.location,
            label: 'LOCATION',
            type: 'getComplexCell',
            getComplexCell: getComplexLocationCell
        },
        {
            id: TableHeadEnum.ipAddress,
            label: 'IP_ADDRESS',
            type: 'getComplexCell',
            getComplexCell: getComplexIpCell
        },
        {
            id: TableHeadEnum.auditSeverity,
            label: 'AUDIT_SEVERITY',
            type: 'getComplexCell',
            getComplexCell: getComplexSeverityCell
        },
        {
            id: TableHeadEnum.signinMethod,
            label: 'SIGN_IN_METHOD',
            type: 'getComplexCell',
            getComplexCell: getComplexAuthmethodCell
        },
        {
            id: TableHeadEnum.warning,
            label: '',
            type: 'getComplexCell',
            width: '70px',
            getComplexCell: getComplexWarningCell
        },
        {
            id: FilterEnum.contains,
            label: 'EVENT_CONTAINS',
            type: 'text',
            filterType: FilterEnum.contains,
            defaultSearchBy: FilterEnum.contains,
            hidden: true
        },
        {
            id: FilterEnum.severity,
            label: 'SEVERITY',
            type: 'text',
            filterType: FilterEnum.severity,
            suggestionsForFilter: severityFilterDropdown,
            hidden: true
        },
        {
            id: FilterEnum.dateRange,
            label: 'SELECT_TIME',
            type: 'text',
            filterType: FilterEnum.dateRange,
            hidden: true
        },
        {
            id: FilterEnum.signinMethod,
            label: 'SIGN_IN_METHOD',
            type: 'text',
            filterType: FilterEnum.signinMethod,
            suggestionsForFilter: signinMethodFilterDropdown,
            hidden: true
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        if (!loadingData && logs) {
            setContinuationToken(logs.ContinuationToken);

            const logsValues: ILogsValue[] = logs.Value;
            let data: ILogsTableData[] = [];

            if (logsValues !== undefined && logsValues.length > 0) {
                for (let log of logsValues) {
                    const rawObject: ILogsFlattened = JSON.parse(log.Raw);

                    const rawString: string = JSON.stringify(rawObject);

                    let logId: string = log.Id;

                    if (!log.Id || log.Id === defaultOrgId) {
                        logId = 'lg-' + Math.floor(Math.random() * 100000 + 1);
                    }

                    let myData = {
                        rawObject: rawObject,
                        raw: rawString,
                        id: logId,
                        created: log.Created,
                        opened: false
                    };

                    data.push(myData);
                }

                setRows(rows.concat(data));
            }
        }
    }, [loadingData, logs]);

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        let continuationTokenToUse = isReset ? '' : continuationToken;
        if (isReset) {
            setRows([]);
            setContinuationToken('');
        }
        dispatch(handleGetLogsData(takeCount, continuationTokenToUse, activeFilters));
    };

    const userAgentResults = (ua: string) => {
        let parser = new UAParser(ua);

        return parser.getResult();
    };

    const isWebService = (ua: string) => {
        return ua.indexOf('MS Web Services') > 0;
    };

    const getOSImage = (ua: string) => {
        const generalOsImage: string = '../../../../assets/images/ua/os.png';

        if (!ua) {
            return generalOsImage;
        }

        const result: any = userAgentResults(ua);

        if (!result || !result.os || !result.os.name || isWebService(ua)) {
            return generalOsImage;
        }

        const os: string = result.os.name.replace(' ', '').toLowerCase();

        return '../../../../assets/images/ua/' + os + '.png';
    };

    const getOS = (ua: string) => {
        const unknownOSTranslation: string = translation('UNKNOWN_OPERATING_SYSTEM');

        if (!ua) {
            return unknownOSTranslation;
        }

        const result: any = userAgentResults(ua);

        if (!result || !result.os || !result.os.name || isWebService(ua)) {
            return unknownOSTranslation;
        }

        return result.os.name + ' ' + result.os.version;
    };

    const getBrowserImage = (ua: string) => {
        const generalBrowserImage: string = '../../../../assets/images/ua/browser.png';

        if (!ua) {
            return generalBrowserImage;
        }

        const result: any = userAgentResults(ua);

        if (!result || !result.browser || !result.browser.name || isWebService(ua)) {
            return generalBrowserImage;
        }

        let browser = result.browser.name.replace(' ', '').toLowerCase();

        return '../../../../assets/images/ua/' + browser + '.png';
    };

    const getBrowser = (ua: string) => {
        const unknownBrowserTranslation: string = translation('UNKNOWN_BROWSER');

        if (!ua) {
            return unknownBrowserTranslation;
        }

        const result: any = userAgentResults(ua);

        if (!result || !result.browser || !result.browser.name || isWebService(ua)) {
            return unknownBrowserTranslation;
        }

        return result.browser.name + ' ' + result.browser.version;
    };

    const showRawEvent = (raw: ILogsFlattened) => {

        let html: any = <RawEventComponent raw={raw} />;
        confirm(html, translation('CLOSE_BUTTON'), undefined, '', false, {size: 'lg'}).then(
            () => {
                // do nothing
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const getProperty = (row: ILogsFlattened, possibleProperties: string): string => {
        let propertyValue: string = '';

        const props = possibleProperties.split(' ');

        props.forEach(function (p) {
            if (propertyValue !== undefined && propertyValue !== '') {
                return;
            }

            propertyValue = row[p];
        });

        return propertyValue;
    };

    const getLocationString = (rawObject: ILogsFlattened): string => {
        let location: string = '';
        if (rawObject.trace_clientIp_city !== undefined && rawObject.trace_clientIp_city !== '') {
            location += rawObject.trace_clientIp_city + ', ';
        }

        if (rawObject.trace_clientIp_state !== undefined && rawObject.trace_clientIp_state !== '') {
            location += rawObject.trace_clientIp_state + ', ';
        }

        if (rawObject.trace_clientIp_country !== undefined && rawObject.trace_clientIp_country !== '') {
            location += rawObject.trace_clientIp_country;
        }

        return location;
    };

    return (
        <>
            {hasOrgPermission([Audit]) ? (
                <>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        headerItems={headCells}
                        rowsCount={continuationToken ? rows.length + 100 : rows.length}
                        onFetch={fetchMoreData}
                        withCheckboxes={false}
                        emptyListHelper={translation('EMPTY_LOG_LIST')}
                        loadingData={logsLoading}
                        hideCounter={true}
                    />
                </>
            ) : (
                <div className='table--empty'>{translation('NO_ORG_LOG_PERMISSIONS')}</div>
            )}
        </>
    );
};

export default LogsPage;
